import React, { useContext, useEffect } from 'react';
import { sendScreenEvent } from '@useblu/blu-lytics';

import PaymentLinkContext from '@/pages/Checkout/context/PaymentLinkContext';

import FormBox from '@/components/FormBox';
import * as FormComponents from '@/components/Template/Form';
import Actions from '@/components/Actions';
import InputControl from '@/components/Shared/InputControl';
import InternalPage from '@/components/InternalPage';

import AddressCEPLoading from './components/AddressCEPLoading';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { states } from '@/constants/states';

import { validationCustomerAddressSchema } from '@/validations/clientPersonalInfoValidation';
import { IUserAddress } from '@/pages/Checkout/types/paymentLinkContextTypes';

const Address: React.FC = () => {
  const {
    setCurrentStep,
    userData,
    setUserData,
    getByCep,
    disabledFields,
    showAddressFields,
    isMobile,
    maxSteps,
  } = useContext(PaymentLinkContext);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    getValues,
    getFieldState,
    formState: { errors },
  } = useForm<IUserAddress>({
    resolver: yupResolver(validationCustomerAddressSchema),
    defaultValues: userData,
  });

  const handleSubmitAddressInformation = (userPayload: IUserAddress) => {
    const data = { ...userData, ...userPayload };
    setUserData(data);
    setCurrentStep('credit');
  };

  const cepApi = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = target;

    getByCep(value, setValue, setError, clearErrors);
  };

  const logEvent = () => {
    if (localStorage._bl_init === 'init') {
      sendScreenEvent('link_blu_checkout_address_view');
      return;
    }

    setTimeout(() => {
      logEvent();
    }, 1000);
  };

  useEffect(() => {
    logEvent();
  }, []);

  return (
    <InternalPage
      columns={8}
      title="Qual o endereço de cobrança?"
      steps={{ currentStep: isMobile ? 3 : 2, steps: maxSteps }}
    >
      <FormBox>
        <form onSubmit={handleSubmit(handleSubmitAddressInformation)}>
          <div>
            <FormComponents.FormRow>
              <FormComponents.FormColumn md="5">
                <InputControl
                  label="CEP"
                  name="cep"
                  onChangeCB={cepApi}
                  placeholder="00000-000"
                  errors={errors}
                  register={register}
                  mask="99999-999"
                  inputMode="numeric"
                  className="input-cep"
                  data-testid="input-cep"
                />
              </FormComponents.FormColumn>
            </FormComponents.FormRow>

            {showAddressFields ? (
              <>
                <FormComponents.FormRow>
                  <FormComponents.FormColumn md="6">
                    <InputControl
                      label="Rua"
                      name="street"
                      placeholder="Ex.: Rua B"
                      errors={errors}
                      register={register}
                      data-testid="input-street"
                    />
                  </FormComponents.FormColumn>
                  <FormComponents.FormColumn md="2">
                    <InputControl
                      label="Número"
                      name="number"
                      placeholder="Ex.: 123"
                      errors={errors}
                      register={register}
                      className="input-number"
                      data-testid="input-number"
                    />
                  </FormComponents.FormColumn>
                  <FormComponents.FormColumn md="4">
                    <InputControl
                      label="Complemento (opcional)"
                      name="complement"
                      placeholder="Ex.: Apto 101"
                      errors={errors}
                      register={register}
                      data-testid="input-complement"
                    />
                  </FormComponents.FormColumn>
                </FormComponents.FormRow>

                <FormComponents.FormRow>
                  <FormComponents.FormColumn md="5">
                    <InputControl
                      label="Bairro"
                      name="district"
                      placeholder="Informe seu bairro"
                      errors={errors}
                      register={register}
                      data-testid="input-district"
                    />
                  </FormComponents.FormColumn>
                  <FormComponents.FormColumn md="5">
                    <InputControl
                      label="Cidade"
                      name="city"
                      placeholder="Informe sua cidade"
                      errors={errors}
                      register={register}
                      disabled={
                        disabledFields.includes('localidade') &&
                        Boolean(getValues('city'))
                      }
                      data-testid="input-city"
                    />
                  </FormComponents.FormColumn>
                  <FormComponents.FormColumn md="2">
                    <Controller
                      name="state"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormComponents.FormSelection
                          {...field}
                          $extended
                          name="state"
                          onChange={option => {
                            clearErrors('state');
                            setValue('state', `${option.value}`);
                          }}
                          label="Estado"
                          value={field.value}
                          defaultValue={userData.state}
                          placeholder="Selecione seu estado"
                          helperText={errors.state ? errors.state.message : ''}
                          error={Boolean(errors.state?.message)}
                          options={states}
                          disabled={
                            disabledFields.includes('uf') &&
                            Boolean(getValues('state'))
                          }
                          data-testid="input-state"
                        />
                      )}
                    />
                  </FormComponents.FormColumn>
                </FormComponents.FormRow>
              </>
            ) : (
              <AddressCEPLoading />
            )}
          </div>

          <Actions
            showBackButtom
            prevStep={() => setCurrentStep('personal')}
            disabled={Boolean(getFieldState('cep').error)}
            prevStepClassname="checkout_botaovoltar1_linkblu"
            nextStepClassname="checkout_botaoavancar2_linkblu"
          />
        </form>
      </FormBox>
    </InternalPage>
  );
};

export default Address;
